import { CookieConsent } from 'src/app/models/consent-data.model'
import { AppConfigGTM, RuntimeEnvType, type AppConfig } from '../models/app-config.model'
import { SupportedLanguages } from '../models/app-translations.model'

export class AppConfigService {
  private static instance: AppConfigService
  private readonly config: AppConfig = {
    saveLogApiUrl: '',
    gtmIntegration: {
      enable: false,
      delayTime: undefined
    },
    displayStickyTrigger: false,
    displayFirstStepRejectAllButton: false,
    displayCloseButton: false,
    currentLang: SupportedLanguages.En,
    customModalClassName: '',
    runtimeEnvType: 'umd',
    entrypointHTMLElementName: 'cookie-info-app',
    consentData: {
      categories: [],
      details_description: '',
      private_policy: ''
    }
  }

  static getInstance(): AppConfigService {
    if (AppConfigService.instance === undefined) {
      AppConfigService.instance = new AppConfigService()
    }

    return AppConfigService.instance
  }

  /**
   * Public API method: set runtime env type
   */
  setRuntimeEnvType(type: RuntimeEnvType): void {
    this.config.runtimeEnvType = type
  }

  /**
   * Public API method: get runtime env type
   */
  getRuntimeEnvType(): RuntimeEnvType {
    return this.config.runtimeEnvType
  }

  /**
   * Public API method: set consent data
   */
  setConsentData(data: CookieConsent): void {
    this.config.consentData = data
  }

  /**
   * Public API method: get consent data
   */
  getConsentData(): CookieConsent {
    return this.config.consentData
  }

  /**
   * Public API method: get entrypoint HTML element name
   */
  getEntrypointHTMLElementName(): string {
    return this.config.entrypointHTMLElementName
  }

  /**
   * Public API method: set api url
   */
  setSaveLogApiUrl(url: string): void {
    this.config.saveLogApiUrl = url
  }

  /**
   * Public API method: get api url
   */
  getSaveLogApiUrl(): string {
    return this.config.saveLogApiUrl
  }

  /**
   * Public API method: set current interface lang
   */
  setInterfaceLang(lang: SupportedLanguages): void {
    this.config.currentLang = lang
  }

  /**
   * Public API method: set current interface lang
   */
  getInterfaceLang(): SupportedLanguages {
    return this.config.currentLang
  }

  /**
   * Public API method: set integration w Google Tag Manager
   */
  setIntegrationWithGTM(delay?: number): void {
    this.config.gtmIntegration = {
      enable: true,
      delayTime: delay
    }
  }

  /**
   * Public API method: get integration w Google Tag Manager state
   */
  getIntegrationWithGTM(): AppConfigGTM {
    return this.config.gtmIntegration
  }

  /**
   * Public API method: set visibility of sticky trigger button
   */
  setDisplayStickyTriggerState(visible: boolean): void {
    this.config.displayStickyTrigger = visible
  }

  /**
   * Public API method: get visibility state of sticky trigger button
   */
  getDisplayStickyTriggerState(): boolean {
    return this.config.displayStickyTrigger
  }

  /**
   * Public API method: set visibility state of reject all button
   */
  setDisplayFirstStepRejectAllButtonState(visible: boolean): void {
    this.config.displayFirstStepRejectAllButton = visible
  }

  /**
   * Public API method: get visibility state of reject all button
   */
  getDisplayFirstStepRejectAllButtonState(): boolean {
    return this.config.displayFirstStepRejectAllButton
  }

  /**
   * Public API method: set visibility state of close button
   */
  setDisplayCloseButtonState(visible: boolean): void {
    this.config.displayCloseButton = visible
  }

  /**
   * Public API method: get visibility state of display close button
   */
  getDisplayCloseButtonState(): boolean {
    return this.config.displayCloseButton
  }

  /**
   * Public API method: set custom css class form modal
   */
  setCustomCssClass(className: string): void {
    this.config.customModalClassName = className
  }

  /**
   * Public API method: get custom css class name of modal
   */
  getCustomCssClass(): string {
    return this.config.customModalClassName
  }
}
